import * as React from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Job, VehicleInput } from "../../../generated/nest-graphql";
import { VehicleHistoryUiState, VehicleHistoryView } from "../VehicleHistoryView";
import { useJobViewMemory } from "../jobViewMemory";
import { useSyncJobVehicleInfo, VehicleSaveScope } from "../../../hooks/useSyncJobVehicleInfo";
import type { JobPageSlots } from "../../../pages/jobs/[id]";
import { SubHeader } from "./SubHeader";
import { TabLabel } from "./TabLabel";
import { CarIcon, DetailsIcon, Tab, TabHeight, Tabs, TabsLayout, TabsMaxWidth } from "../../ui";

export enum JobPageTab {
  DETAILS = "DETAILS",
  HISTORY = "HISTORY",
}

/** How long a restored scroll offset waits without the page growing before giving up. */
const RESTORE_TIMEOUT_MS = 3000;
/** Upper bound for a page that never loads, so a restore cannot hold the scroll for good. */
const RESTORE_MAX_WAIT_MS = 20000;
const RESTORE_RETRY_MS = 50;

export const JobVehicleHistoryTabs: React.FC<{ renderJobPage: (slots: JobPageSlots) => React.ReactNode }> = ({
  renderJobPage,
}) => {
  const { id } = useParams<{ id: string }>();
  // Per job, and kept above the routes: a job opened for the first time starts on Details, and coming back
  // to one — from another job, customer history or an invoice — restores its tab, the history's section and
  // toggles, and its scroll offset.
  const memory = useJobViewMemory();
  // Each job mounts fresh (AnimatedRoutes), so its remembered tab is read once and written through.
  const [tab, setTab] = useState(() => memory.tabByJob[id] ?? JobPageTab.DETAILS);
  const rememberUiState = useCallback(
    (state: VehicleHistoryUiState) => {
      memory.uiStateByJob[id] = state;
    },
    [memory, id]
  );
  const getInitialUiState = useCallback(() => memory.uiStateByJob[id], [memory, id]);

  // The history stays mounted while hidden once used on this visit to the job, so an in-flight Carfax
  // refresh survives a look at Details; it re-reads its lists each time it is shown again. Each job gets a
  // fresh mount (see AnimatedRoutes), so coming back to one on Details does not load a hidden history.
  const historyOpened = useRef(false);
  // The job as the page last rendered it, so the save callback stays stable and the memoised history view
  // does not re-render with every page render.
  const latestJob = useRef<Job | null>(null);
  const syncJobVehicle = useSyncJobVehicleInfo();
  const syncSavedVehicle = useCallback(
    async (vehicle: VehicleInput, scope?: VehicleSaveScope) => {
      if (latestJob.current) await syncJobVehicle(latestJob.current, vehicle, scope);
    },
    [syncJobVehicle]
  );
  if (tab === JobPageTab.HISTORY) historyOpened.current = true;

  const selectTab = (next: JobPageTab) => {
    if (next === tab) return;
    memory.tabByJob[id] = next;
    setTab(next);
  };

  // Both tabs scroll the window, so the offset is kept per job and tab. A view seen for the first time opens
  // at the top. Coming back to one waits for its content — the job and the history both load after a
  // remount — before scrolling, and records nothing meanwhile so the empty page's clamped 0 is not saved.
  const view = `${id}:${tab}`;
  const currentView = useRef(view);
  const restoring = useRef(false);
  const restoreTimer = useRef<number>();
  // A layout effect, so the cleanup runs before the next job's first scroll: React 17 defers passive cleanups,
  // and this listener would otherwise save that scroll as this job's offset.
  useLayoutEffect(() => {
    const remember = () => {
      if (!restoring.current) memory.scrollByView[currentView.current] = window.scrollY;
    };
    const stopRestoring = () => {
      restoring.current = false;
    };
    window.addEventListener("scroll", remember, { passive: true });
    // Any scroll the technician starts wins over the restore.
    const userScrollEvents = ["touchstart", "wheel", "keydown", "pointerdown"];
    userScrollEvents.forEach((event) => window.addEventListener(event, stopRestoring, { passive: true }));
    return () => {
      window.removeEventListener("scroll", remember);
      userScrollEvents.forEach((event) => window.removeEventListener(event, stopRestoring));
      window.clearTimeout(restoreTimer.current);
    };
  }, [memory]);
  // Which views have their content: Details once the page has its job, History once its lists have loaded.
  const loadedViews = useRef<Record<string, boolean>>({});
  const markHistoryLoaded = useCallback(() => {
    loadedViews.current[`${id}:${JobPageTab.HISTORY}`] = true;
  }, [id]);

  // Runs on mount too: arriving at a job starts at its saved place, or the top. Nothing counts down until the
  // view says its content is there — the job and the history load one after the other after a remount — and
  // the countdown restarts whenever the page still grows.
  useLayoutEffect(() => {
    currentView.current = view;
    const target = memory.scrollByView[view] ?? 0;
    window.clearTimeout(restoreTimer.current);
    restoring.current = true;
    const startedAt = Date.now();
    let lastHeight = document.documentElement.scrollHeight;
    let settlingSince: number | null = null;
    const attempt = () => {
      if (!restoring.current) return;
      window.scrollTo(0, target);
      const now = Date.now();
      const height = document.documentElement.scrollHeight;
      if (loadedViews.current[view] && (settlingSince === null || height > lastHeight)) settlingSince = now;
      lastHeight = height;
      const stalled = settlingSince !== null && now - settlingSince > RESTORE_TIMEOUT_MS;
      if (window.scrollY >= target - 1 || stalled || now - startedAt > RESTORE_MAX_WAIT_MS) {
        restoring.current = false;
        return;
      }
      restoreTimer.current = window.setTimeout(attempt, RESTORE_RETRY_MS);
    };
    attempt();
  }, [view, memory]);

  const [jobHeaderBottom, setJobHeaderBottom] = useState(0);

  const subHeader = (
    <SubHeader onJobHeaderBottom={setJobHeaderBottom}>
      <Tabs
        value={tab}
        onChange={selectTab}
        layout={TabsLayout.FULL_WIDTH}
        maxWidth={TabsMaxWidth.COMPACT}
        height={TabHeight.COMPACT}
        aria-label="Job sections"
      >
        <Tab
          value={JobPageTab.DETAILS}
          id="job-tab-details"
          aria-controls="job-panel-details"
          label={<TabLabel Icon={DetailsIcon} label="Job" />}
          height={TabHeight.COMPACT}
          style={{ textTransform: "none", fontWeight: 600, fontSize: "0.7875rem" }}
        />
        <Tab
          value={JobPageTab.HISTORY}
          id="job-tab-vehicle"
          aria-controls="job-panel-vehicle"
          label={<TabLabel Icon={CarIcon} label="Vehicle" />}
          height={TabHeight.COMPACT}
          style={{ textTransform: "none", fontWeight: 600, fontSize: "0.7875rem" }}
        />
      </Tabs>
    </SubHeader>
  );

  // Details stays mounted while hidden so an in-progress form or inspection survives a tab switch.
  // The job comes from the page, which already has it, so the tab needs no query of its own.
  const renderContent = (details: React.ReactNode, job: Job) => {
    loadedViews.current[`${id}:${JobPageTab.DETAILS}`] = true;
    latestJob.current = job;
    return (
      <>
        <div
          hidden={tab !== JobPageTab.DETAILS}
          role="tabpanel"
          id="job-panel-details"
          aria-labelledby="job-tab-details"
        >
          {details}
        </div>
        {historyOpened.current && (
          <div
            hidden={tab !== JobPageTab.HISTORY}
            role="tabpanel"
            id="job-panel-vehicle"
            aria-labelledby="job-tab-vehicle"
          >
            <VehicleHistoryView
              vehicleId={job.vehicleId ? String(job.vehicleId) : null}
              fallbackVehicleInfo={job.vehicleInfo}
              contactId={job.contact?.id ?? null}
              currentJobId={id}
              getInitialUiState={getInitialUiState}
              onUiStateChange={rememberUiState}
              visible={tab === JobPageTab.HISTORY}
              onLoaded={markHistoryLoaded}
              stickyTop={jobHeaderBottom}
              onVehicleSaved={syncSavedVehicle}
            />
          </div>
        )}
      </>
    );
  };

  // The beta also moves the job's status pill into the header, coloured by status.
  return <>{renderJobPage({ subHeader, renderContent, vehicleInOwnTab: true, statusInHeader: true })}</>;
};
